import React, { useState } from 'react';
import { TEMPLATES, MATERIALS, MACHINES } from '../engineeringRules';
import { GitBranch, Book, Beaker } from 'lucide-react';

export default function EngineeringRules() {
  const [tab, setTab] = useState<'templates' | 'materials' | 'rules'>('templates');

  return (
    <div className="fade-in">
      <div className="section-header">
        <h1>Engineering Rules</h1>
        <span className="badge badge-orange">v2.1 · March 2026</span>
      </div>
      <p style={{ color: 'var(--forge-text2)', fontSize: 12, marginBottom: 20 }}>
        Derived from analysis of 60+ production drawings across 6 machine families, 8 part templates.
      </p>

      <div className="tabs">
        <button className={`tab ${tab === 'templates' ? 'active' : ''}`} onClick={() => setTab('templates')}><GitBranch size={11} style={{ marginRight: 4 }} />Templates</button>
        <button className={`tab ${tab === 'materials' ? 'active' : ''}`} onClick={() => setTab('materials')}><Beaker size={11} style={{ marginRight: 4 }} />Materials</button>
        <button className={`tab ${tab === 'rules' ? 'active' : ''}`} onClick={() => setTab('rules')}><Book size={11} style={{ marginRight: 4 }} />Key Rules</button>
      </div>

      {tab === 'templates' && (
        <div className="grid-2">
          {Object.values(TEMPLATES).map(t => (
            <div key={t.id} className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 24, fontWeight: 800, fontFamily: 'var(--font-display)', color: 'var(--forge-accent)', letterSpacing: '-0.04em', lineHeight: 1 }}>{t.id}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{t.name}</div>
                  <span className={`badge ${t.family === 'BOLT' ? 'badge-orange' : 'badge-blue'}`} style={{ marginTop: 3 }}>{t.family} · {t.stations} stations</span>
                </div>
              </div>
              <p style={{ fontSize: 12, color: 'var(--forge-text2)', marginBottom: 10 }}>{t.description}</p>
              <div>
                {t.triggers.map((tr, i) => (
                  <div key={i} style={{ fontSize: 11, color: 'var(--forge-text3)', padding: '2px 0' }}>
                    <span style={{ color: 'var(--forge-accent)' }}>›</span> {tr}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'materials' && (
        <div className="card" style={{ padding: 0 }}>
          <table>
            <thead>
              <tr>
                <th>Material</th>
                <th>Grade</th>
                <th>σ₀ (MPa)</th>
                <th>K (MPa)</th>
                <th>n</th>
                <th>HT Quench</th>
                <th>HT Temper</th>
                <th>Target HRC</th>
                <th>Application</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(MATERIALS).map(m => (
                <tr key={m.id}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 500 }}>{m.id}</td>
                  <td><span className="tag">{m.grade}</span></td>
                  <td className="mono-val" style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{m.sigma_0_mpa}</td>
                  <td className="mono-val" style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{m.k_mpa}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{m.n}</td>
                  <td style={{ fontSize: 11 }}>{m.ht_quench_c || '—'}</td>
                  <td style={{ fontSize: 11 }}>{m.ht_temper_c || '—'}</td>
                  <td style={{ fontSize: 11 }}>{m.target_hrc || '—'}</td>
                  <td style={{ color: 'var(--forge-text2)', fontSize: 11 }}>{m.application}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'rules' && (
        <div className="grid-2">
          {KEY_RULES.map((section, i) => (
            <div key={i} className="card">
              <h3 style={{ marginBottom: 12, color: 'var(--forge-accent)' }}>{section.title}</h3>
              {section.rules.map((rule, j) => (
                <div key={j} style={{ padding: '6px 0', borderBottom: j < section.rules.length - 1 ? '1px solid var(--forge-border)' : 'none' }}>
                  <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--forge-text)', marginBottom: 2 }}>{rule.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--forge-text3)' }}>{rule.rule}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const KEY_RULES = [
  { title: 'Template Selection', rules: [
    { name: 'L/D ratio', rule: 'L/D > 4.0 → Template A; L/D < 3.5 → Template B; 3.5–4.0 → borderline, engineer review' },
    { name: 'Set screw detection', rule: 'head_type = NONE + socket_type = HEX_SOCKET → Template C, 2 stations only' },
    { name: 'Nut routing', rule: 'part_type = NUT → D-series template. No L/D ratio needed.' },
    { name: 'Hex formation station', rule: 'Plain hex nut (D2): hex at Stn 2. Serrated flange nut (D1): hex at Stn 3. One station earlier for plain hex.' },
  ]},
  { title: 'Cone Angle Rule', rules: [
    { name: 'Long bolt (Template A)', rule: '6° cone if wire_dia < 11mm; 9° cone if wire_dia ≥ 11mm' },
    { name: 'Short flange bolt (Template B)', rule: 'Always 9° regardless of wire diameter' },
    { name: 'Correction from WOOTZ M10', rule: '9° confirmed on M10 (Ø9.5mm wire) for Template B — overrides the ≥11mm rule' },
  ]},
  { title: 'KO Pin Rules', rules: [
    { name: 'Machine constant (length)', rule: '11B6S=85mm, 14B6S=110mm, 19B6S=130mm, 24B6S=170mm. Bolt formers=215mm.' },
    { name: 'Body OD is part-specific', rule: 'KO body OD ≈ die bore − 0.10mm. NOT a machine constant.' },
    { name: 'Active KO stations', rule: 'Nut formers: active KO at Stns 4 & 5 universally. Plain ejection at Stns 1, 2, 3.' },
    { name: '11B6S exception', rule: 'Station 4 KO is 60mm (vs 85mm standard) due to flanged positive-stop geometry.' },
  ]},
  { title: 'TC Pallet Selection', rules: [
    { name: '≤10mm wire', rule: 'Front die: Ø20×40 — Middle/back: Ø20×40' },
    { name: '10–13mm wire', rule: 'Front die: Ø25×50 — Middle die: Ø25×50 or Ø25×40 — Back die: Ø25×40' },
    { name: '13–16mm wire', rule: 'All stations: Ø28×50' },
    { name: 'D2 front insert', rule: 'When external hex OD contacts die bore on exit (Stn 3+ for ext hex), use D2 front insert, NOT TC' },
  ]},
  { title: 'Punch Insert Materials', rules: [
    { name: 'Hex pin punch', rule: 'M42 if socket A/F ≤ 7mm; M2 if A/F > 7mm. Set screws: always M42.' },
    { name: 'Coller Hex Trim Die', rule: 'M2 always. Template A-EXT station 3.' },
    { name: 'Pallet for logo/grade', rule: 'Carbide Ø35×35. Standard (no marking): Steel Ø35×35.' },
    { name: 'Template B punch inserts', rule: 'All forming stations: D2 insert. Final trim: M2. EXT punch used on small formers.' },
  ]},
  { title: 'Die Entry Angle Progression', rules: [
    { name: 'Nut formers (all)', rule: 'Stn 1: 35°, Stns 2–4: 30°, Stn 5: 5° taper only (no entry cone)' },
    { name: 'Plain hex nut (D2)', rule: '35°→30°→30°→30°→5° across Stns 1–5. Confirmed on M10 (19B6S) and M14 (24B6S)' },
    { name: 'Springback allowance', rule: 'Final die A/F = target A/F − 0.10 to −0.15mm for mild steel nuts' },
  ]},
  { title: 'Stripper Mechanism (Template C)', rules: [
    { name: '8 components mandatory', rule: 'Stripper Case (H13), Bush (H13), Hex Punch Holder (D2), Stripper Pin Holder (D2), Back Filler (D2), Sleeve (H13), Stripper Pins ×3 (M2, Ø3.95–3.98mm), PCD Ø32mm at 120°' },
    { name: 'Stripper bush', rule: 'Ø39.98-0.02 / Ø34.98-0.02 × 35mm — constant across machines' },
    { name: 'Hex punch holder', rule: 'Ø44.98 -0.02 × 20mm, 3-hole PCD Ø32mm at 120° — constant' },
  ]},
  { title: 'Process Flow Templates', rules: [
    { name: 'Set screw (SCM-435)', rule: 'Forging → Threading (Circular Rolling) → H/T (Q870-880, T≥390, 45-49HRC) → Mn.Phosphate+RPO → Inspect → Dispatch' },
    { name: 'Bolt Grade 8.8 (15B25)', rule: 'Forging → H/T (Q870-880, T450-530, 23-32HRC) → Threading (Flat Rolling) → WZP Plating → Inspect → Dispatch' },
    { name: 'Bolt Grade 10.9', rule: 'Forging → H/T → Surface Treatment → Inspect → Dispatch' },
    { name: 'Nut Grade 8', rule: 'Forging → Tapping → Surface Treatment (Zinc/Black) → Inspect → Dispatch' },
  ]},
];
